/* eslint-disable no-continue */
/* eslint-disable func-names */
import Highcharts from 'highcharts';
import lodashMerge from 'lodash/merge';
import moment from 'moment';
import utilService from '../../../utils/util.service';
import { formatNumber } from '../formatNumber';

// Type definitions
export interface DonutDataItem {
    name: string;
    y: number;
    color: string;
    urn?: string;
    // Optional value shown in the label/tooltip instead of `y`. Lets a slice keep a
    // non-zero `y` purely to render its ring geometry while honestly displaying its real
    // value (e.g. a "no entities" placeholder that draws a full ring but reads 0).
    displayY?: number;
    events?: {
        mouseOver?: () => void;
        mouseOut?: () => void;
    };
}

export interface ChartSeriesData {
    name: string;
    data: number[];
    color?: string;
}

export interface TimeSeriesResult {
    categories: string[];
    data: number[];
    total: number;
}

// Base default configuration for all charts
const BASE_CHART_CONFIG: Highcharts.Options = {
    chart: {
        type: 'area',
        animation: false,
        reflow: true,
        style: {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            height: '100%',
            fontFamily: 'LLCirc',
        },
        spacing: [0, 0, 0, 0],
    },
    title: {
        text: '',
    },
    plotOptions: {
        area: {
            marker: {
                enabled: false,
                symbol: 'circle',
                radius: 2,
                states: {
                    hover: {
                        enabled: true,
                    },
                },
            },
            fillOpacity: 0.05,
        },
        areaspline: {
            marker: {
                enabled: false,
            },
            fillOpacity: 0.05,
        },
        line: {
            marker: {
                enabled: false,
                radius: 4,
                states: {
                    hover: {
                        enabled: true,
                    },
                },
            },
            lineWidth: 2,
        },
        series: {
            pointPlacement: 'on',
        },
    },
    xAxis: {
        lineWidth: 0,
        gridLineWidth: 0,
        gridLineColor: '#E9E9EA',
        labels: {
            style: {
                fontSize: '14px',
                fontWeight: '300',
                color: 'rgba(98, 100, 105, 1)',
            },
        },
    },
    yAxis: {
        title: {
            text: '',
        },
        gridLineWidth: 1,
        labels: {
            style: {
                fontSize: '14px',
                fontWeight: '300',
                color: 'rgba(98, 100, 105, 1)',
            },
        },
    },
    credits: {
        enabled: false,
    },
    tooltip: {
        shared: true,
        backgroundColor: '#ffffff',
        borderWidth: 1,
        borderColor: 'rgba(0, 0, 0, 0.19)',
        borderRadius: 4,
        useHTML: true,
        style: {
            color: '#000000',
            fontSize: '14px',
            fontFamily: 'inherit',
            opacity: 1,
        },
        headerFormat: '<div style="font-size: 16px; color: #1F2229;">{point.key}</div>',
        pointFormat: '<div style="color: #626469; font-size: 14px;">{series.name}: <b>{point.y}</b></div>',
        footerFormat: '',
        dateTimeLabelFormats: {
            day: '%a, %b %e, %Y',
            week: 'Week from %a, %b %e, %Y',
            hour: '%a, %b %e, %H:%M',
        },
        shadow: false,
    },
};

// Specific chart type configurations
const MULTILINE_AREA_CONFIG: Highcharts.Options = {
    chart: {},
    xAxis: {
        categories: ['-2.5h', '-2h', '-1.5h', '-1h', '-30m', 'Now'],
    },
    plotOptions: {
        areaspline: {
            marker: {
                enabled: false,
                radius: 2,
                states: {
                    hover: {
                        enabled: true,
                        radius: 4,
                    },
                },
            },
            lineWidth: 2,
        },
        spline: {
            marker: {
                enabled: false,
                radius: 2,
                states: {
                    hover: {
                        enabled: true,
                        radius: 4,
                    },
                },
            },
            lineWidth: 2,
            dashStyle: 'Dash',
        },
    },
};

const STACKED_COLUMN_CONFIG: Highcharts.Options = {
    chart: {
        type: 'column',
    },
    plotOptions: {
        column: {
            stacking: 'normal',
            pointWidth: 4,
            borderRadius: 2,
            borderWidth: 0,
        },
    },
    xAxis: {
        tickLength: 0,
    },
};

const SIMPLE_COLUMN_CONFIG: Highcharts.Options = {
    chart: {
        type: 'column',
        spacing: [25, 0, 0, 0],
    },
    plotOptions: {
        column: {
            pointWidth: 4,
            groupPadding: 0,
            pointPadding: 0,
            borderRadius: 2,
        },
    },
    xAxis: {
        tickLength: 0,
    },
    legend: {
        enabled: false,
    },
};

const INTERACTIVE_DONUT_CONFIG = {
    chart: {
        type: 'pie',
    },
    plotOptions: {
        pie: {
            innerSize: '95%',
            borderRadius: 0,
            borderWidth: 4,
            borderColor: 'white',
            allowPointSelect: false,
            cursor: 'pointer',
            dataLabels: {
                enabled: true,
                useHTML: true,
                alignTo: 'plotEdges',
                style: {
                    fontFamily: 'LLCirc',
                    fontWeight: '300',
                    fontStyle: 'Book',
                    fontSize: '14px',
                },
            },
            showInLegend: false,
            states: {
                hover: {
                    halo: {
                        size: 0,
                    },
                },
            },
        },
    },
} as unknown as Highcharts.Options;

// Extended chart interface for custom properties
interface ExtendedChart extends Highcharts.Chart {
    customText?: Highcharts.SVGElement;
    customSubText?: Highcharts.SVGElement;
    customTextCleanupAdded?: boolean;
}

// Extended point interface for shape manipulation
interface ExtendedPoint {
    shapeType?: string;
    shapeArgs?: {
        x?: number;
        y?: number;
        width?: number;
        height?: number;
        d?: (string | number)[];
    };
}

// Extended series interface
interface ExtendedSeries {
    options: {
        stacking?: string;
    };
    type: string;
    points: ExtendedPoint[];
}

// Function to enable rounded stacks
function enableRoundedStacks(): void {
    // Access column prototype via any to avoid TypeScript issues with internal Highcharts types
    const HC = Highcharts as typeof Highcharts & {
        seriesTypes: {
            column: {
                prototype: Highcharts.Series;
                __roundedPatchApplied?: boolean;
            };
        };
    };

    // Prevent duplicate wrapping
    if (HC.seriesTypes?.column?.__roundedPatchApplied) return;
    if (HC.seriesTypes?.column) {
        HC.seriesTypes.column.__roundedPatchApplied = true;
    }

    Highcharts.wrap(
        HC.seriesTypes.column.prototype,
        'translate',
        function translateWrapper(this: ExtendedSeries, proceed: () => void) {
            proceed.call(this);

            const { options, type, points } = this;
            const { stacking } = options;
            const isStacked = stacking === 'normal' || stacking === 'percent';
            const isColumnOrBar = type === 'column' || type === 'bar';

            if (!isStacked || !isColumnOrBar) return;

            const pointsLength = points.length;

            for (let i = 0; i < pointsLength; i += 1) {
                const point = points[i];
                const shape = point.shapeArgs;

                if (!shape || (shape.height ?? 0) <= 0) continue;

                const { x = 0, y = 0, width = 0, height = 0 } = shape;

                const dynamicRadius = Math.min(width / 2, height * 0.25, 10);
                const overlapAmount = 5;
                const adjustedY = y - overlapAmount;
                const adjustedHeight = height + overlapAmount;

                if (dynamicRadius < 1) continue;

                point.shapeType = 'path';
                point.shapeArgs = {
                    d: [
                        'M',
                        x,
                        adjustedY + dynamicRadius,
                        'A',
                        dynamicRadius,
                        dynamicRadius,
                        0,
                        0,
                        1,
                        x + dynamicRadius,
                        adjustedY,
                        'L',
                        x + width - dynamicRadius,
                        adjustedY,
                        'A',
                        dynamicRadius,
                        dynamicRadius,
                        0,
                        0,
                        1,
                        x + width,
                        adjustedY + dynamicRadius,
                        'L',
                        x + width,
                        adjustedY + adjustedHeight,
                        'L',
                        x,
                        adjustedY + adjustedHeight,
                        'Z',
                    ],
                };
            }
        },
    );
}

// Function to create donut center text renderer
function createDonutCenterTextRenderer(mainText: string, subText: string): Highcharts.ChartEventsOptions['render'] {
    return function renderCenterText(this: Highcharts.Chart) {
        const chart = this as ExtendedChart;
        const centerX = chart.plotWidth / 2 + chart.plotLeft;
        const centerY = chart.plotHeight / 2 + chart.plotTop + 10;

        // Constrain the center label to the donut hole so longer translations
        // (e.g. French) word-wrap inside the ring instead of overflowing it.
        // Hole diameter ~= background pie size (90%) * its innerSize (74%) of the pie diameter.
        const pieDiameter = Math.min(chart.plotWidth, chart.plotHeight);
        const holeDiameter = pieDiameter * 0.9 * 0.74;
        const maxLabelWidth = Math.max(0, Math.floor(holeDiameter * 0.85));

        // Clean up existing elements
        if (chart.customText) {
            chart.customText.destroy();
            chart.customText = undefined;
        }
        if (chart.customSubText) {
            chart.customSubText.destroy();
            chart.customSubText = undefined;
        }

        // Add cleanup on chart destroy if not already added
        if (!chart.customTextCleanupAdded) {
            const originalDestroy = chart.destroy.bind(chart);
            chart.destroy = function destroyWithCleanup() {
                if (chart.customText) {
                    chart.customText.destroy();
                    chart.customText = undefined;
                }
                if (chart.customSubText) {
                    chart.customSubText.destroy();
                    chart.customSubText = undefined;
                }
                originalDestroy();
            };
            chart.customTextCleanupAdded = true;
        }

        chart.customText = chart.renderer
            .text(utilService.encodeEscapeCharacters(mainText), centerX, centerY - 12)
            .css({
                fontFamily: 'ITCAvantG, Arial, sans-serif',
                fontWeight: '500',
                fontSize: '40px',
                color: 'rgba(53, 56, 62, 1)',
            })
            .attr({
                align: 'center',
                'text-anchor': 'middle',
            })
            .add();

        chart.customSubText = chart.renderer
            .text(utilService.encodeEscapeCharacters(subText), centerX, centerY + 12)
            .css({
                fontFamily: 'LLCirc, Arial, sans-serif',
                fontWeight: '300',
                fontSize: '14px',
                color: 'rgba(31, 34, 41, 1)',
                width: `${maxLabelWidth}px`,
                textAlign: 'center',
            })
            .attr({
                align: 'center',
                'text-anchor': 'middle',
            })
            .add();

        // When the label wraps to multiple lines, shift it up so the block stays
        // vertically centered under the main number instead of drifting downward.
        const subTextBBox = chart.customSubText.getBBox();
        const singleLineHeight = 16;
        if (subTextBBox.height > singleLineHeight) {
            chart.customSubText.attr({ y: centerY + 12 - (subTextBBox.height - singleLineHeight) / 2 });
        }
    };
}

// Helper function to convert color to lower opacity
function convertColorToLowerOpacity(color: string, targetOpacity = 0.1): string {
    // Handle rgba format
    if (color.startsWith('rgba(')) {
        return color.replace(/,\s*[\d.]+\)$/, `, ${targetOpacity})`);
    }
    // Handle rgb format - convert to rgba
    if (color.startsWith('rgb(')) {
        return color.replace('rgb(', 'rgba(').replace(')', `, ${targetOpacity})`);
    }
    // Handle hex colors - convert to rgba
    if (color.startsWith('#')) {
        const hex = color.substring(1);
        let r: number;
        let g: number;
        let b: number;

        if (hex.length === 3) {
            r = parseInt(hex[0] + hex[0], 16);
            g = parseInt(hex[1] + hex[1], 16);
            b = parseInt(hex[2] + hex[2], 16);
        } else if (hex.length === 6) {
            r = parseInt(hex.substring(0, 2), 16);
            g = parseInt(hex.substring(2, 4), 16);
            b = parseInt(hex.substring(4, 6), 16);
        } else {
            return `rgba(128, 128, 128, ${targetOpacity})`;
        }

        return `rgba(${r}, ${g}, ${b}, ${targetOpacity})`;
    }
    // Fallback
    return color.replace('1)', `${targetOpacity})`);
}

function createDonutLabelFormatter(unit = ''): Highcharts.DataLabelsFormatterCallbackFunction {
    return function formatDonutLabel(this: Highcharts.PointLabelObject): string {
        const { point } = this;
        const bgColor = convertColorToLowerOpacity(point.color as string);
        const { displayY } = point.options as DonutDataItem;
        const rawCount = typeof displayY === 'number' ? displayY : (point.y as number);
        const formattedCount = String(formatNumber(rawCount));
        const value = unit ? `${formattedCount} ${unit}` : formattedCount;
        const encodedPointName = utilService.encodeEscapeCharacters(point.name);
        const titleAttr = ` title="${utilService.encodeEscapeCharacters(point.name)}"`;
        return `<div style="text-align: center;"><div${titleAttr} style="font-family: LLCirc; font-weight: 300; font-size: 14px; color: #333; max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${encodedPointName}</div><div style="font-family: LLCirc; font-weight: 300; font-size: 14.35px; color: ${point.color}; background-color: ${bgColor}; padding: 2px 4px; border-radius: 4px; margin: 2px auto 0; min-width: 24px; width: fit-content;">${value}</div></div>`;
    };
}
// Function to create interactive donut series with hover effects
function createInteractiveDonutSeries(
    mainSeriesData: DonutDataItem[],
    seriesName = 'Data',
    onPointClick?: (name: string, urn?: string) => void,
): Highcharts.SeriesPieOptions[] {
    // Create background series data from main series
    const backgroundSeriesData = mainSeriesData.map((item) => ({
        name: item.name,
        y: item.y,
        color: convertColorToLowerOpacity(item.color),
    }));

    return [
        {
            type: 'pie' as const,
            name: seriesName,
            size: '100%',
            innerSize: '90%',
            data: mainSeriesData.map((item, index) => ({
                ...item,
                events: {
                    mouseOver(this: Highcharts.Point) {
                        const { series } = this;
                        const innerSeries = series.chart.series[1];
                        if (innerSeries && innerSeries.points[index]) {
                            innerSeries.points[index].update(
                                {
                                    color: this.color as string,
                                },
                                true,
                                false,
                            );
                        }
                    },
                    mouseOut(this: Highcharts.Point) {
                        const { series } = this;
                        const innerSeries = series.chart.series[1];
                        const bgColor = convertColorToLowerOpacity(this.color as string);
                        if (innerSeries && innerSeries.points[index]) {
                            innerSeries.points[index].update(
                                {
                                    color: bgColor,
                                },
                                true,
                                false,
                            );
                        }
                    },
                    ...(onPointClick && {
                        click() {
                            onPointClick(item.name, item.urn);
                        },
                    }),
                },
            })),
        },
        {
            type: 'pie' as const,
            name: `${seriesName} Background`,
            size: '90%',
            innerSize: '74%',
            enableMouseTracking: false,
            borderRadius: 4,
            dataLabels: {
                enabled: false,
            },
            data: backgroundSeriesData,
        } as Highcharts.SeriesPieOptions,
    ];
}

// Utility functions to create complete chart configurations
function createMultilineAreaChartConfig(
    seriesData: Highcharts.SeriesOptionsType[],
    additionalConfig: Highcharts.Options = {},
): Highcharts.Options {
    return lodashMerge({}, BASE_CHART_CONFIG, MULTILINE_AREA_CONFIG, { series: seriesData }, additionalConfig);
}

function createStackedColumnChartConfig(
    seriesData: Highcharts.SeriesOptionsType[],
    additionalConfig: Highcharts.Options = {},
): { chartConfig: Highcharts.Options; enableRoundedStacks: boolean } {
    return {
        chartConfig: lodashMerge(
            {},
            BASE_CHART_CONFIG,
            STACKED_COLUMN_CONFIG,
            { series: seriesData },
            additionalConfig,
        ),
        enableRoundedStacks: true,
    };
}

function createSimpleColumnChartConfig(
    seriesData: Highcharts.SeriesOptionsType[],
    additionalConfig: Highcharts.Options = {},
): Highcharts.Options {
    return lodashMerge({}, BASE_CHART_CONFIG, SIMPLE_COLUMN_CONFIG, { series: seriesData }, additionalConfig);
}

function createInteractiveDonutChartConfig(
    seriesData: DonutDataItem[],
    centerText: string,
    subText: string,
    unit = '',
    seriesName = 'Data',
    additionalConfig: Highcharts.Options = {},
    onPointClick?: (name: string, urn?: string) => void,
    spacing: [number, number, number, number] = [20, 50, 20, 50],
): Highcharts.Options {
    const series = createInteractiveDonutSeries(seriesData, seriesName, onPointClick);
    const unitSuffix = unit ? ` ${unit}` : '';
    const config = lodashMerge(
        {},
        BASE_CHART_CONFIG,
        INTERACTIVE_DONUT_CONFIG,
        {
            chart: {
                spacing,
                events: {
                    render: createDonutCenterTextRenderer(centerText, subText),
                },
            },
            plotOptions: {
                pie: {
                    dataLabels: {
                        formatter: createDonutLabelFormatter(unit),
                        ...(onPointClick && { style: { cursor: 'pointer' } }),
                    },
                },
            },
            series,
            tooltip: {
                shared: false,
                outside: true,
                backgroundColor: 'rgba(255, 255, 255, 1)',
                formatter(this: Highcharts.TooltipFormatterContextObject) {
                    const { displayY } = this.point.options as DonutDataItem;
                    const rawY = typeof displayY === 'number' ? displayY : (this.y as number);
                    const formattedY = String(formatNumber(rawY));
                    const valueWithUnit = unitSuffix ? `${formattedY}${unitSuffix}` : formattedY;
                    const encodedKey = utilService.encodeEscapeCharacters(this.point.name || '');
                    // A zero display value has no meaningful share of the whole, so don't
                    // surface the geometry-driven percentage (which would read 100%).
                    const hasPercentage = rawY !== 0 && typeof this.percentage === 'number';
                    const percentage = hasPercentage ? (this.percentage as number).toFixed(1) : '0.0';
                    return `<div style="font-size: 16px; color: #1F2229; max-width: 138px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${encodedKey}</div>${this.series.name}: <b>${valueWithUnit}</b><br/>Percentage: <b>${percentage}%</b>`;
                },
            },
        },
        additionalConfig,
    );
    return config;
}

/**
 * Transform reporting API response to time series data
 * @param apiResponse - Array of objects with row properties
 * @param xKey - Property to display on x-axis
 * @param yKey - Property to display on y-axis
 */
function transformReportingToTimeSeriesData<T extends Record<string, unknown>>(
    apiResponse: T[],
    xKey: keyof T,
    yKey: keyof T,
): TimeSeriesResult {
    if (!apiResponse || !Array.isArray(apiResponse)) {
        return {
            categories: [],
            data: [],
            total: 0,
        };
    }

    const result = apiResponse.reduce<TimeSeriesResult>(
        (acc, item) => {
            const xValue = item[xKey];
            const yValue = (item[yKey] as number) || 0;

            const formattedX = moment(xValue as string | number | Date).format('MMM DD');

            acc.categories.push(formattedX);
            acc.data.push(yValue);
            acc.total += yValue;
            return acc;
        },
        { categories: [], data: [], total: 0 },
    );

    return result;
}

export type SpacingTuple = [number, number, number, number];

function getResponsiveSpacing(): SpacingTuple {
    const w = window.innerWidth;
    /**
     * Top/bottom stays fixed regardless of viewport width. The donut's chart height is a
     * fixed pixel value (not responsive), so the vertical room available for the 12/6
     * o'clock data labels never actually grows or shrinks with viewport width - only the
     * horizontal room does. Highcharts also skips its normal "shrink the pie to fit the
     * labels" step whenever an explicit `size` is set (as our series do), so those labels
     * are placed at their natural distance from the ring with no automatic compensation.
     * Previously this tier reduced top/bottom spacing along with left/right, which left too
     * little room for the two-line label (name + value badge) and the connector's default
     * 30px distance, clipping labels on narrower (e.g. 13") laptop screens.
     */
    const verticalSpacing = 20;
    if (w < 1400) return [verticalSpacing, 20, verticalSpacing, 20];
    if (w < 1600) return [verticalSpacing, 35, verticalSpacing, 35];
    return [verticalSpacing, 50, verticalSpacing, 50];
}

// Export service object
export const chartConfigUtilService = {
    // Base configurations
    BASE_CHART_CONFIG,
    MULTILINE_AREA_CONFIG,
    STACKED_COLUMN_CONFIG,
    SIMPLE_COLUMN_CONFIG,
    INTERACTIVE_DONUT_CONFIG,

    // Utility functions
    enableRoundedStacks,
    createDonutCenterTextRenderer,
    createDonutLabelFormatter,
    createInteractiveDonutSeries,
    convertColorToLowerOpacity,

    // Chart creation functions
    createMultilineAreaChartConfig,
    createStackedColumnChartConfig,
    createSimpleColumnChartConfig,
    createInteractiveDonutChartConfig,

    // Data transformation functions
    transformReportingToTimeSeriesData,

    // Responsive helpers
    getResponsiveSpacing,
};

export default chartConfigUtilService;
